import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { supabase } from '../services/supabase';

type TemplateChannel = 'sms' | 'email';
type TemplateStatus = 'draft' | 'active' | 'archived';
type FlowStatus = 'draft' | 'active' | 'paused' | 'archived';
type CampaignStatus = 'draft' | 'scheduled' | 'archived';
type CampaignChannel = 'sms' | 'email';

export interface MarketingTemplate {
  id: string;
  name: string;
  channel: TemplateChannel;
  subject: string | null;
  body: string;
  status: TemplateStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingFlow {
  id: string;
  name: string;
  description: string | null;
  status: FlowStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingFlowStep {
  id: string;
  flow_id: string;
  step_order: number;
  step_type: string;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  channel: CampaignChannel;
  template_id: string | null;
  flow_id: string | null;
  status: CampaignStatus;
  scheduled_at: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingKeyword {
  id: string;
  keyword: string;
  description: string | null;
  response_template_id: string | null;
  flow_id: string | null;
  subscription_channel_id: string | null;
  is_active: boolean;
  trigger_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingSubscriptionChannel {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingSubscriberMembership {
  id: string;
  channel_id: string;
  source: string;
  subscribed_at: string;
  unsubscribed_at: string | null;
  channel: Pick<MarketingSubscriptionChannel, 'id' | 'name'> | null;
}

export interface MarketingSubscriber {
  id: string;
  phone_e164: string;
  first_name: string | null;
  last_name: string | null;
  source: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  channel_memberships?: MarketingSubscriberMembership[];
}

export interface MarketingTagSet {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const TABLES = {
  templates: 'marketing_templates',
  flows: 'marketing_flows',
  flowSteps: 'marketing_flow_steps',
  campaigns: 'marketing_campaigns',
  keywords: 'marketing_keywords',
  channels: 'marketing_subscription_channels',
  subscribers: 'marketing_subscribers',
  memberships: 'marketing_subscriber_channel_memberships',
  tagSets: 'marketing_tag_sets',
} as const;

const KEY = {
  templates: ['marketing', 'templates'] as const,
  flows: ['marketing', 'flows'] as const,
  flowSteps: (flowId: string) => ['marketing', 'flow-steps', flowId] as const,
  campaigns: ['marketing', 'campaigns'] as const,
  keywords: ['marketing', 'keywords'] as const,
  channels: ['marketing', 'channels'] as const,
  subscribers: ['marketing', 'subscribers'] as const,
  memberships: ['marketing', 'memberships'] as const,
  tagSets: ['marketing', 'tag-sets'] as const,
};

export const useMarketingTemplates = () =>
  useQuery<MarketingTemplate[]>({
    queryKey: KEY.templates,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.templates)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingTemplate[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingTemplate = () =>
  useMutation({
    mutationFn: async (input: {
      name: string;
      channel: TemplateChannel;
      subject?: string | null;
      body: string;
      status?: TemplateStatus;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.templates)
        .insert({
          name: input.name,
          channel: input.channel,
          subject: input.subject ?? null,
          body: input.body,
          status: input.status ?? 'draft',
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.templates }),
  });

export const useUpdateMarketingTemplate = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<Pick<MarketingTemplate, 'name' | 'subject' | 'body' | 'status'>>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.templates)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.templates }),
  });

export const useDeleteMarketingTemplate = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.templates).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.templates }),
  });

export const useMarketingFlows = () =>
  useQuery<MarketingFlow[]>({
    queryKey: KEY.flows,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.flows)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingFlow[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingFlow = () =>
  useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string | null;
      status?: FlowStatus;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.flows)
        .insert({
          name: input.name,
          description: input.description ?? null,
          status: input.status ?? 'draft',
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingFlow;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.flows }),
  });

export const useUpdateMarketingFlow = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<Pick<MarketingFlow, 'name' | 'description' | 'status'>>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.flows)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingFlow;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.flows }),
  });

export const useDeleteMarketingFlow = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.flows).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.flows });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'flow-steps'] });
    },
  });

export const useMarketingFlowSteps = (flowId?: string) =>
  useQuery<MarketingFlowStep[]>({
    queryKey: KEY.flowSteps(flowId ?? 'none'),
    queryFn: async () => {
      if (!flowId) return [];
      const { data, error } = await supabase
        .from(TABLES.flowSteps)
        .select('*')
        .eq('flow_id', flowId)
        .order('step_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as MarketingFlowStep[];
    },
    staleTime: 30_000,
  });

export const useMarketingFlowStepCounts = (flowIds: string[]) =>
  useQuery<Record<string, number>>({
    queryKey: ['marketing', 'flow-step-counts', ...flowIds],
    queryFn: async () => {
      if (flowIds.length === 0) return {};
      const { data, error } = await supabase
        .from(TABLES.flowSteps)
        .select('flow_id')
        .in('flow_id', flowIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of data ?? []) {
        const flowId = (row as { flow_id: string }).flow_id;
        counts[flowId] = (counts[flowId] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 30_000,
  });

export const useCreateMarketingFlowStep = () =>
  useMutation({
    mutationFn: async (input: {
      flowId: string;
      stepOrder: number;
      stepType: string;
      config?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.flowSteps)
        .insert({
          flow_id: input.flowId,
          step_order: input.stepOrder,
          step_type: input.stepType,
          config: input.config ?? {},
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingFlowStep;
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: KEY.flowSteps(input.flowId) }),
  });

export const useUpdateMarketingFlowStep = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      flowId: string;
      changes: Partial<Pick<MarketingFlowStep, 'step_order' | 'step_type' | 'config'>>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.flowSteps)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingFlowStep;
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: KEY.flowSteps(input.flowId) }),
  });

export const useDeleteMarketingFlowStep = () =>
  useMutation({
    mutationFn: async (input: { id: string; flowId: string }) => {
      const { error } = await supabase.from(TABLES.flowSteps).delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: KEY.flowSteps(input.flowId) }),
  });

export const useMarketingCampaigns = () =>
  useQuery<MarketingCampaign[]>({
    queryKey: KEY.campaigns,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.campaigns)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingCampaign[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingCampaign = () =>
  useMutation({
    mutationFn: async (input: {
      name: string;
      channel: CampaignChannel;
      templateId?: string | null;
      flowId?: string | null;
      scheduledAt?: string | null;
      status?: CampaignStatus;
      metadata?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.campaigns)
        .insert({
          name: input.name,
          channel: input.channel,
          template_id: input.templateId ?? null,
          flow_id: input.flowId ?? null,
          scheduled_at: input.scheduledAt ?? null,
          status: input.status ?? 'draft',
          metadata: input.metadata ?? {},
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingCampaign;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.campaigns }),
  });

export const useUpdateMarketingCampaign = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<
        Pick<MarketingCampaign, 'name' | 'channel' | 'status' | 'template_id' | 'flow_id' | 'scheduled_at'>
      >;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.campaigns)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingCampaign;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.campaigns }),
  });

export const useDeleteMarketingCampaign = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.campaigns).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.campaigns }),
  });

export const useMarketingKeywords = () =>
  useQuery<MarketingKeyword[]>({
    queryKey: KEY.keywords,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.keywords)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingKeyword[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingKeyword = () =>
  useMutation({
    mutationFn: async (input: {
      keyword: string;
      description?: string | null;
      responseTemplateId?: string | null;
      flowId?: string | null;
      subscriptionChannelId?: string | null;
      isActive?: boolean;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.keywords)
        .insert({
          keyword: input.keyword.trim().toUpperCase(),
          description: input.description ?? null,
          response_template_id: input.responseTemplateId ?? null,
          flow_id: input.flowId ?? null,
          subscription_channel_id: input.subscriptionChannelId ?? null,
          is_active: input.isActive ?? true,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingKeyword;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.keywords }),
  });

export const useUpdateMarketingKeyword = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<
        Pick<
          MarketingKeyword,
          'description' | 'response_template_id' | 'flow_id' | 'subscription_channel_id' | 'is_active'
        >
      > & { keyword?: string };
    }) => {
      const changes = { ...input.changes };
      if (changes.keyword) {
        changes.keyword = changes.keyword.trim().toUpperCase();
      }
      const { data, error } = await supabase
        .from(TABLES.keywords)
        .update(changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingKeyword;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.keywords }),
  });

export const useDeleteMarketingKeyword = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.keywords).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.keywords }),
  });

export const useMarketingSubscriptionChannels = () =>
  useQuery<MarketingSubscriptionChannel[]>({
    queryKey: KEY.channels,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.channels)
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as MarketingSubscriptionChannel[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingSubscriptionChannel = () =>
  useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string | null;
      isActive?: boolean;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.channels)
        .insert({
          name: input.name.trim(),
          description: input.description ?? null,
          is_active: input.isActive ?? true,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingSubscriptionChannel;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.channels }),
  });

export const useMarketingSubscribers = () =>
  useQuery<MarketingSubscriber[]>({
    queryKey: KEY.subscribers,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.subscribers)
        .select(
          `
          *,
          channel_memberships:${TABLES.memberships}(
            id,
            channel_id,
            source,
            subscribed_at,
            unsubscribed_at,
            channel:${TABLES.channels}(id, name)
          )
          `
        )
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingSubscriber[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingSubscriber = () =>
  useMutation({
    mutationFn: async (input: {
      phoneE164: string;
      firstName?: string | null;
      lastName?: string | null;
      source?: string;
      isActive?: boolean;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.subscribers)
        .upsert(
          {
            phone_e164: input.phoneE164,
            first_name: input.firstName ?? null,
            last_name: input.lastName ?? null,
            source: input.source ?? 'manual',
            is_active: input.isActive ?? true,
          },
          { onConflict: 'phone_e164' }
        )
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingSubscriber;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.subscribers }),
  });

export const useAssignSubscriberToChannel = () =>
  useMutation({
    mutationFn: async (input: {
      subscriberId: string;
      channelId: string;
      source?: string;
      unsubscribedAt?: string | null;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.memberships)
        .upsert(
          {
            subscriber_id: input.subscriberId,
            channel_id: input.channelId,
            source: input.source ?? 'manual',
            unsubscribed_at: input.unsubscribedAt ?? null,
          },
          { onConflict: 'subscriber_id,channel_id' }
        )
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.subscribers });
      queryClient.invalidateQueries({ queryKey: KEY.memberships });
    },
  });

export const useMarketingTagSets = () =>
  useQuery<MarketingTagSet[]>({
    queryKey: KEY.tagSets,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.tagSets)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingTagSet[];
    },
    staleTime: 30_000,
  });

export const useCreateMarketingTagSet = () =>
  useMutation({
    mutationFn: async (input: { name: string; description?: string | null; color?: string | null }) => {
      const { data, error } = await supabase
        .from(TABLES.tagSets)
        .insert({
          name: input.name,
          description: input.description ?? null,
          color: input.color ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingTagSet;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.tagSets }),
  });

export const useUpdateMarketingTagSet = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<Pick<MarketingTagSet, 'name' | 'description' | 'color'>>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.tagSets)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MarketingTagSet;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.tagSets }),
  });

export const useDeleteMarketingTagSet = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.tagSets).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.tagSets }),
  });
